/**
 * Microphone Context
 *
 * Provides microphone state to the component tree via React Context.
 * Wraps the useMicrophone hook so it runs once in the provider,
 * and all children consume the shared state via useMicrophoneContext().
 *
 * Mount the provider high in the tree (e.g., alongside AudioRecorderProvider)
 * so that all microphone-aware components have access.
 */
import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';

import {
  type UseMicrophoneReturn,
  useMicrophone,
} from '../hooks/useMicrophone';

const MicrophoneCtx = createContext<UseMicrophoneReturn | null>(null);

export interface MicrophoneProviderProps {
  children: ReactNode;
}

/**
 * Provider that initialises microphone state (permission check, device
 * enumeration, event listeners) and shares the result with descendants.
 */
export const MicrophoneProvider = ({ children }: MicrophoneProviderProps) => {
  const mic = useMicrophone();

  return (
    <MicrophoneCtx.Provider value={mic}>{children}</MicrophoneCtx.Provider>
  );
};

/**
 * Read microphone state from the nearest MicrophoneProvider.
 *
 * @throws if called outside a MicrophoneProvider
 */
export function useMicrophoneContext(): UseMicrophoneReturn {
  const ctx = useContext(MicrophoneCtx);
  if (!ctx) {
    throw new Error(
      'useMicrophoneContext must be used within a MicrophoneProvider',
    );
  }
  return ctx;
}
